import React from 'react';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Box, IconButton, Stack, Tooltip, Paper, Popper, Typography } from '@mui/material';
import { DataTable } from '@pihealth/common-ui-library';
import { useUser } from 'src/components/hooks/useUser';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import { GridRenderCellParams } from '@mui/x-data-grid-pro';
import { GridToolbarExport } from '@mui/x-data-grid-pro';
import moment from 'moment';
interface GridCellExpandProps {
  value: string;
  width: number;
}

const GridCellExpand = React.memo(function GridCellExpand(props: GridCellExpandProps) {
  const { width, value } = props;
  const wrapper: any = React.useRef<HTMLDivElement | null>(null);
  const cellDiv = React.useRef(null);
  const cellValue: any = React.useRef(null);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [showFullCell, setShowFullCell] = React.useState(false);
  const [showPopper, setShowPopper] = React.useState(false);
  function isOverflown(element: Element): boolean {
    return element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth;
  }
  const handleMouseEnter = () => {
    const isCurrentlyOverflown = isOverflown(cellValue.current);
    setShowPopper(isCurrentlyOverflown);
    setAnchorEl(cellDiv.current);
    setShowFullCell(true);
  };
  const handleMouseLeave = () => {
    setShowFullCell(false);
  };
  React.useEffect(() => {
    if (!showFullCell) {
      return undefined;
    }
    function handleKeyDown(nativeEvent: KeyboardEvent) {
      // IE11, Edge (prior to using Bink?) use 'Esc'
      if (nativeEvent.key === 'Escape' || nativeEvent.key === 'Esc') {
        setShowFullCell(false);
      }
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [setShowFullCell, showFullCell]);
  return (
    <Box
      ref={wrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{
        alignItems: 'center',
        lineHeight: '24px',
        width: '100%',
        height: '100%',
        position: 'relative',
        display: 'flex'
      }}
    >
      <Box
        ref={cellDiv}
        sx={{
          height: '100%',
          width,
          display: 'block',
          position: 'absolute',
          top: 0
        }}
      />
      <Box ref={cellValue} sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
        {value}
      </Box>
      {showPopper && (
        <Popper open={showFullCell && anchorEl !== null} anchorEl={anchorEl} style={{ width, marginLeft: -17 }}>
          <Paper elevation={6} style={{ minHeight: wrapper.current?.offsetHeight - 3 }}>
            <Typography variant="body1" style={{ padding: 12, wordBreak: 'break-word' }}>
              {value}
            </Typography>
          </Paper>
        </Popper>
      )}
    </Box>
  );
});
function renderCellExpand(params: GridRenderCellParams<string>) {
  return <GridCellExpand value={params.value || ''} width={params.colDef.computedWidth} />;
}

const DataGridDeviationLog: React.FunctionComponent<any> = (props: any) => {
  const {
    rows,
    checkboxSelection,
    pageNumber,
    setPageNumber,
    selectionModel,
    onSelectionModelChange,
    CustomFooterButtons,
    deviationLogPermissions,
    handelEdit,
    handleDeleteOpen,
    currentDiviationLevel,
    isLoading,
    apiRef,
    pageSize,
    setPageSize,
    rowsCount,
    setFilterObject
  } = props;
  const { preferedDateFormat, userPreferedDateFormat } = usePreferedDateFormat();

  const formatDate = (params: any) => {
    return preferedDateFormat(params.value);
  };
  const { user, email } = useUser();
  const currentUserWithEmail = `${user} (${email})`;
  const columns = [
    {
      field: 'deviation_no',
      headerName: 'Deviation Number',
      width: 100,
      renderCell: (params: any) => {
        return (
          <div style={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <span className="artNameinTd">{params.formattedValue}</span>
            {(params.row.action_due < new Date().toLocaleDateString('en-CA') &&
              params.row.action_completion_dt === null && <WarningAmberIcon color="error" />) ||
              (params.row.decision_due_dt < new Date().toLocaleDateString('en-CA') &&
                params.row.decision_comp_dt === null && <WarningAmberIcon color="error" />)}
          </div>
        );
      }
    },
    {
      field: 'study_id',
      headerName: 'Study',
      width: 120,
      renderCell: renderCellExpand
    },
    {
      field: 'country',
      headerName: 'Country',
      width: 80,
      renderCell: renderCellExpand
    },
    {
      field: 'site_id',
      headerName: 'Site',
      width: 100,
      renderCell: renderCellExpand
    },
    {
      field: 'subject_id',
      headerName: 'Subject ID',
      minWidth: 110,
      flex: 1,
      renderCell: (params: any) => (
        <Tooltip title={params.row.subject_id}>
          <span className="table-cell-trucate">{params.row.subject_id}</span>
        </Tooltip>
      )
    },
    {
      field: 'dt_deviation',
      headerName: 'Date Identified',
      type: 'date',
      valueGetter: formatDate,
      minWidth: 100,
      flex: 1
    },
    {
      field: 'deviation_des',
      headerName: 'Protocol Deviation Description',
      minWidth: 200,
      flex: 1,
      renderCell: renderCellExpand
    },
    {
      field: 'category',
      headerName: 'Category of Deviation',
      minWidth: 100,
      flex: 1
    },
    {
      field: 'severity',
      headerName: 'Severity',
      minWidth: 90,
      flex: 1
    },
    {
      field: 'reported_irb',
      headerName: 'Reported to IRB/IEC?',
      width: 120,
      type: 'boolean',
      renderCell: (params: any) => (
        <Stack direction="row" spacing={1}>
          {params.row?.reported_irb ? (
            <IconButton title="Reported">
              <CheckCircleRoundedIcon color="success" />
            </IconButton>
          ) : null}
        </Stack>
      )
    },
    {
      field: 'date_irb',
      headerName: 'Date Reported to IRB',
      width: 120,
      type: 'date',
      valueGetter: formatDate
    },
    {
      field: 'action_req',
      headerName: 'Action Required?',
      width: 100,
      renderCell: renderCellExpand,
      filterable: false
    },
    {
      field: 'action_des',
      headerName: 'Action Description',
      width: 140,
      renderCell: renderCellExpand
    },
    {
      field: 'action_taken',
      headerName: 'Action Taken',
      width: 120,
      renderCell: renderCellExpand
    },
    {
      field: 'action_assigned_to',
      headerName: 'Action Assigned To',
      width: 140,
      renderCell: renderCellExpand
    },
    {
      field: 'action_due',
      headerName: 'Action Due Date',
      width: 100,
      type: 'date',
      valueGetter: formatDate
    },
    {
      field: 'action_completion_dt',
      headerName: 'Date of Action Completion',
      width: 120,
      type: 'date',
      valueGetter: formatDate
    },
    {
      field: 'decision_req',
      headerName: 'Decision Required?',
      width: 100,
      renderCell: renderCellExpand,
      filterable: false
    },
    {
      field: 'decision',
      headerName: 'Decisions',
      width: 140,
      renderCell: renderCellExpand
    },
    {
      field: 'decision_update',
      headerName: 'Decision Update',
      width: 120,
      renderCell: renderCellExpand
    },
    {
      field: 'decision_assigned_to',
      headerName: 'Decision Assigned To',
      width: 140,
      renderCell: renderCellExpand
    },
    {
      field: 'decision_due_dt',
      headerName: 'Decision Due Date',
      width: 100,
      type: 'date',
      valueGetter: formatDate
    },
    {
      field: 'decision_comp_dt',
      headerName: 'Date of Decision Completion',
      width: 140,
      expandable: true,
      type: 'date',
      valueGetter: formatDate
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      sortable: false,
      width: 80,
      print: false,
      renderCell: (params: any) => {
        const filterActionUsers =
          params.row.action_assigned_to.length <= 0
            ? params.row.action_assigned_to
            : params.row.action_assigned_to.filter((name: string) => name === currentUserWithEmail);
        const filterDecisionUsers =
          params.row.decision_assigned_to.length <= 0
            ? params.row.decision_assigned_to
            : params.row.decision_assigned_to.filter((name: string) => name === currentUserWithEmail);

        return (
          <Stack direction="row" spacing={0}>
            {(params.row.created_by === user ? deviationLogPermissions.update : deviationLogPermissions.updateOther) ||
            (deviationLogPermissions.updateAction && (filterActionUsers.length > 0 ? true : false)) ||
            (deviationLogPermissions.updateDecision && (filterDecisionUsers.length > 0 ? true : false)) ? (
              <IconButton color="primary" onClick={() => handelEdit(params.row)} title="Edit">
                <EditIcon />
              </IconButton>
            ) : (
              ''
            )}
            {(params.row.created_by === user ? deviationLogPermissions.update : deviationLogPermissions.deleteOther) ? (
              <IconButton color="primary" onClick={() => handleDeleteOpen(params.row)} title="Delete">
                <DeleteIcon />
              </IconButton>
            ) : (
              ''
            )}
          </Stack>
        );
      }
    }
  ];
  const definedCustomDatGridExport = (exportFields: any = {}) => {
    return (
      <GridToolbarExport
        csvOptions={{
          fileName: `${`Protocol Deviation Log`} ${moment(new Date()).format(
            `${(userPreferedDateFormat.includes('/')
              ? userPreferedDateFormat.replaceAll('/', '_')
              : userPreferedDateFormat.replaceAll('-', '_')
            ).toUpperCase()} hh_mm A`
          )}`
        }}
      />
    );
  };
  const sponsorColumns = [
    {
      field: 'pd_identification_options',
      headerName: 'PD Identification Method Options',
      width: 180,
      expandable: true,
      renderCell: renderCellExpand
    },
    {
      field: 'action_taken_analysis',
      headerName: 'Action Taken for Analysis',
      width: 140,
      expandable: true,
      renderCell: renderCellExpand
    }
  ];
  const columnValue = currentDiviationLevel == 'SITE' ? columns : [...columns, ...sponsorColumns];
  const dataTableProps: any = {
    apiRef,
    sx: {
      '& .MuiDataGrid-columnHeaderTitle': {
        whiteSpace: 'break-spaces !important',
        lineHeight: 1.3,
        '&:hover': {
          textOverflow: 'ellipsis'
        }
      }
    },
    filterMode: 'server'
  };
  const filterValues = (data: any) => {
    data.filters.forEach((item: { operation: string; name: string; value: string | boolean | undefined }) => {
      if (item.name === 'reported_irb') {
        if (item.value === undefined || item.value === '') {
          item.value = undefined;
          item.operation = 'is_not';
        } else {
          item.value = item.value === 'true';
        }
      }
    });
    setFilterObject(data);
  };
  return (
    <>
      <DataTable
        rows={rows}
        columns={columnValue}
        hasDefinedColumns
        loading={isLoading}
        datePreference={userPreferedDateFormat}
        paging
        rowsCount={rowsCount}
        pageNumber={pageNumber}
        pageSize={pageSize}
        setPageSize={setPageSize}
        setPageNumber={setPageNumber}
        paginationMode="server"
        className="studylogDatagrid"
        exportAll
        height="74vh"
        disableSelectionOnClick
        checkboxSelection={checkboxSelection}
        selectionModel={selectionModel}
        onRowsSelection={onSelectionModelChange}
        CustomFooter={CustomFooterButtons}
        checkboxSelectionVisibleOnly
        dataTableProps={dataTableProps}
        pinnedColumns={{
          right: ['actions']
        }}
        hasDefinedDataGridToolbarExport
        definedGridToolbarExport={definedCustomDatGridExport}
        filterValues={(data: any) => filterValues(data)}
      />
    </>
  );
};
export default DataGridDeviationLog;
